import React from 'react';
import { Link } from 'react-router-dom';
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';
import _ from 'lodash';
import { calcRegion } from '../../../utils/CTUtils';
import ActivityLoader from '../../ActivityLoader';
import AlertDialog from '../../AlertDialog';
import UserProfileDropdown from '../../UserProfileDropdown';
import headerBarStyles from '../../css/Header.module.css';
import domesticStyles from './HeaderDomestic.module.css';
import '../../../pages/css/domesticTheme.css';
import { hasChatPermission } from './headerProfileGroups';
import { AssumeUserModal, RemoveAssumeUserModal } from './HeaderAssumeUserModals';
import DomesticSubNav from './DomesticSubNav';
import { useDomesticHeaderState } from './useDomesticHeaderState';
import { useDomesticHeaderSpacer } from './useDomesticHeaderSpacer';
import logo from '../../../assets/images/logo.png';

/**
 * Logged-in domestic Ohana portal shell (top nav + optional booking/tracking sub-bar).
 * Callbacks are owned by the parent Header class.
 */
export default function DomesticLoggedInHeader({
  user,
  assumeuser,
  location,
  assumeUserFieldType,
  showLoader,
  showAlertDialog,
  alertDialogMsg,
  alertDialogButtons,
  onAlertDialogClose,
  isShowingModal,
  txtAssumeUser,
  onAssumeModalClose,
  onAssumeTextChange,
  onAssumeSubmit,
  isShowingModalUnassume,
  onUnassumeModalNo,
  onUnassumeConfirm,
  changeLanguage,
  userProfile,
  infoCenter,
  changePassword,
  didAdminAccountSearch,
  didAdminProfileSearch,
  didAdminEtaRuleSearch,
  didAdminEtaRuleExceptionSearch,
  onOpenAssumeUserModal,
  onReleaseAssumeUser,
  userGuide,
  didSupportHelp,
  onLogout,
  t,
  i18nLanguage,
  renderAccountStatusBanner,
}) {
  const {
    profile,
    access,
    subNav,
    cognitoGroups,
    subLinks,
    spacerClass,
    subLinkClass,
  } = useDomesticHeaderState({ user, assumeuser, location });
  const { headerRef, spacerStyle } = useDomesticHeaderSpacer();

  const {
    profileGroups,
    displayName: profileDisplayName,
    email: profileEmail,
    initial: profileInitial,
    picture: profilePicture,
  } = profile;

  const navTitleClass = (active) =>
    active ? headerBarStyles.titleText_Bold : headerBarStyles.titleText;

  const navDropdownClass = (active) =>
    [
      domesticStyles.navDropdown,
      active ? domesticStyles.navDropdownActive : '',
    ].join(' ');

  return (
    <>
      {showLoader && <ActivityLoader show={showLoader} />}
      {showAlertDialog && (
        <AlertDialog
          variant="domestic"
          show={showAlertDialog}
          msg={(alertDialogMsg || '').split('||')[0]}
          title={(alertDialogMsg || '').split('||')[1]}
          buttons={alertDialogButtons}
          didClose={onAlertDialogClose}
        />
      )}
      <header
        ref={headerRef}
        className={[
          domesticStyles.shell,
          subNav.legacyPageTitleKey ? domesticStyles.shellWithLegacyTitle : '',
          'domestic-design-scope',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Navbar
          bg="white"
          expand="lg"
          className={domesticStyles.domesticNavbarMain}
        >
          <Navbar.Brand className="d-flex align-items-center py-0 me-0 me-lg-2">
            <Nav.Link
              as={Link}
              to="/"
              href="/"
              className={domesticStyles.brandLink}
            >
              <img
                className={domesticStyles.logo}
                height="44"
                src={logo}
                alt="Matson"
              />
            </Nav.Link>
          </Navbar.Brand>
          {_.includes(cognitoGroups, 'CSR') && assumeuser !== null ? (
            <p className={`mb-0 ${domesticStyles.assumeStrip}`}>
              Assumed User ID:
              <span className={domesticStyles.assumeName}>
                {assumeuser.username}
              </span>
            </p>
          ) : null}
          <Navbar.Toggle
            aria-controls="ohana-domestic-nav"
            className={domesticStyles.toggle}
          />
          <Navbar.Collapse
            id="ohana-domestic-nav"
            className={`justify-content-lg-center align-items-lg-center flex-grow-1 ${domesticStyles.collapseNav}`}
          >
            <div
              className={`d-flex flex-column flex-lg-row flex-wrap align-items-lg-center justify-content-lg-center flex-lg-grow-1 gap-1 ${domesticStyles.primaryNav}`}
            >
              {access.showContainerSearchInMainNav ? (
                <Link
                  to={access.intlContainerSearchPath}
                  className={headerBarStyles.navLink}
                >
                  <p className={navTitleClass(subNav.isContainerSearchMainActive)}>
                    {t('header.containerSearch')}
                  </p>
                </Link>
              ) : null}
              {access.hasShipmentTracking ? (
                access.hasIntlShipment && access.hasDomShipment ? (
                  <NavDropdown
                    title={t('header.shipmentTracking')}
                    id="domestic-dd-shipment-tracking"
                    className={navDropdownClass(subNav.isShipmentTrackingActive)}
                  >
                    <NavDropdown.Item as={Link} to={access.intlShipmentPath}>
                      {t('header.shipmentTrackingInternational')}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/d/shipment">
                      {t('header.shipmentTrackingDomestic')}
                    </NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <Link
                    to={
                      access.hasIntlShipment
                        ? access.intlShipmentPath
                        : '/d/shipment'
                    }
                    className={headerBarStyles.navLink}
                  >
                    <p className={navTitleClass(subNav.isShipmentTrackingActive)}>
                      {t('header.shipmentTracking')}
                    </p>
                  </Link>
                )
              ) : null}
              {access.hasBooking ? (
                access.hasIntlBooking && access.hasDomBooking ? (
                  <NavDropdown
                    title={t('header.booking')}
                    id="domestic-dd-booking"
                    className={navDropdownClass(subNav.isBookingActive)}
                  >
                    <NavDropdown.Item as={Link} to={access.intlBookingPath}>
                      {t('header.bookingPortalInternational')}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/d/booking">
                      {t('header.bookingPortalDomestic')}
                    </NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <Link
                    to={
                      access.hasIntlBooking
                        ? access.intlBookingPath
                        : '/d/booking'
                    }
                    className={headerBarStyles.navLink}
                  >
                    <p className={navTitleClass(subNav.isBookingActive)}>
                      {t('header.booking')}
                    </p>
                  </Link>
                )
              ) : null}
              {access.hasShippingInstruction ? (
                access.hasIntlShippingInstruction && access.hasDomSI ? (
                  <NavDropdown
                    title={t('header.shippingInstruction')}
                    id="domestic-dd-shipping-instruction"
                    className={navDropdownClass(subNav.isSiActive)}
                  >
                    <NavDropdown.Item
                      as={Link}
                      to={access.intlShippingInstructionPath}
                    >
                      {t('header.shippingInstructionPortalInternational')}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/d/shipping-instruction">
                      {t('header.shippingInstructionPortalDomestic')}
                    </NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <Link
                    to={
                      access.hasIntlShippingInstruction
                        ? access.intlShippingInstructionPath
                        : '/d/shipping-instruction'
                    }
                    className={headerBarStyles.navLink}
                  >
                    <p className={navTitleClass(subNav.isSiActive)}>
                      {t('header.shippingInstruction')}
                    </p>
                  </Link>
                )
              ) : null}
              {access.hasVgm ? (
                <Link
                  to="/vgmDetails"
                  className={headerBarStyles.navLink}
                >
                  <p className={navTitleClass(subNav.isVgmActive)}>
                    {t('header.vgmDetails')}
                  </p>
                </Link>
              ) : null}
              {access.hasDocument ? (
                access.hasIntlDocument && access.hasDomDocument ? (
                  <NavDropdown
                    title={t('header.document')}
                    id="domestic-dd-doc"
                    className={navDropdownClass(subNav.isDocActive)}
                  >
                    <NavDropdown.Item as={Link} to={access.intlDocumentPath}>
                      {t('header.documentInternational')}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/d/billingdocs">
                      {t('header.documentDomestic')}
                    </NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <Link
                    to={
                      access.hasIntlDocument
                        ? access.intlDocumentPath
                        : '/d/billingdocs'
                    }
                    className={headerBarStyles.navLink}
                  >
                    <p className={navTitleClass(subNav.isDocActive)}>
                      {t('header.document')}
                    </p>
                  </Link>
                )
              ) : null}
              {access.hasVS ? (
                <Link
                  to="/salesView"
                  className={headerBarStyles.navLink}
                >
                  <p className={navTitleClass(subNav.isSalesViewActive)}>
                    {t('header.salesView')}
                  </p>
                </Link>
              ) : null}
              {access.hasRep ? (
                <Link
                  to="/repSalesView"
                  className={headerBarStyles.navLink}
                >
                  <p className={navTitleClass(subNav.isRepSalesViewActive)}>
                    {t('header.repSalesViewMenu')}
                  </p>
                </Link>
              ) : null}
              {access.hasAccountStatement ? (
                <Link
                  to={access.accountStatementPath}
                  className={headerBarStyles.navLink}
                >
                  <p className={navTitleClass(subNav.isAccountStatementActive)}>
                    {t('header.accountStatement')}
                  </p>
                </Link>
              ) : null}
            </div>
            <div className={`ms-lg-3 mt-2 mt-lg-0 ${domesticStyles.profileSlot}`}>
              <UserProfileDropdown
                displayName={profileDisplayName}
                email={profileEmail}
                avatarUrl={profilePicture}
                avatarInitial={profileInitial}
                currentLanguage={i18nLanguage}
                onLanguageChange={changeLanguage}
                showUserProfile={!!profileGroups}
                onUserProfile={userProfile}
                showInformationCenter={
                  user.continent_code === 'AS' ||
                  process.env.REACT_APP_STAGE !== 'production'
                }
                informationCenterLabel={t('header.informationCenter')}
                onInformationCenter={infoCenter}
                onChangePassword={changePassword}
                showAdminUserSearch={_.includes(profileGroups, 'ADMIN')}
                onAdminUserSearch={didAdminAccountSearch}
                showAdminCpSearch={_.includes(profileGroups, 'ADMIN')}
                onAdminCpSearch={didAdminProfileSearch}
                showEtaRuleMaintenance={_.includes(profileGroups, 'ADMIN')}
                onEtaRuleMaintenance={didAdminEtaRuleSearch}
                showEtaExceptionMaintenance={_.includes(
                  profileGroups,
                  'ADMIN'
                )}
                onEtaExceptionMaintenance={didAdminEtaRuleExceptionSearch}
                showAssumeUser={_.includes(profileGroups, 'CSR')}
                assumeUserIsActive={assumeuser != null}
                onAssumeUser={onOpenAssumeUserModal}
                onReleaseAssumeUser={onReleaseAssumeUser}
                showUserGuide={
                  calcRegion(window.location.href) === 'CN' &&
                  (_.includes(profileGroups, 'SI') ||
                    _.includes(profileGroups, 'BD'))
                }
                onUserGuide={userGuide}
                showChat={hasChatPermission(user, assumeuser)}
                onContactUs={didSupportHelp}
                onLogout={onLogout}
                version={process.env.REACT_APP_VERSION}
              />
            </div>
          </Navbar.Collapse>
        </Navbar>
        {subNav.showDomesticSub && subNav.showIntlBookingSub ? (
          <DomesticSubNav
            ariaLabel="International booking"
            links={subLinks.intlBooking}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showDomBookingSub ? (
          <DomesticSubNav
            ariaLabel="Booking"
            links={subLinks.domBooking}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showIntlShipmentSub ? (
          <DomesticSubNav
            ariaLabel="International shipment tracking"
            links={subLinks.intlShipment}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showDomShipmentSub ? (
          <DomesticSubNav
            ariaLabel="Shipment tracking"
            links={subLinks.domShipment}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showIntlSiSub ? (
          <DomesticSubNav
            ariaLabel="International shipping instruction"
            links={subLinks.intlShippingInstruction}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showDomSiSub ? (
          <DomesticSubNav
            ariaLabel="Domestic shipping instruction"
            links={subLinks.domShippingInstruction}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showIntlDocSub ? (
          <DomesticSubNav
            ariaLabel="International document"
            links={subLinks.intlDocument}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showDomDocSub ? (
          <DomesticSubNav
            ariaLabel="Domestic document"
            links={subLinks.domDocument}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.showDomesticSub && subNav.showDomAccountStatementSub ? (
          <DomesticSubNav
            ariaLabel="Account statement"
            links={subLinks.domAccountStatement}
            subLinkClass={subLinkClass}
            t={t}
          />
        ) : null}
        {subNav.legacyPageTitleKey ? (
          <div className={domesticStyles.legacyTitleBar}>
            <h1 className={domesticStyles.legacyTitle}>
              {t(subNav.legacyPageTitleKey)}
            </h1>
          </div>
        ) : null}
      </header>
      <div className={spacerClass} style={spacerStyle} aria-hidden />
      {isShowingModal && (
        <AssumeUserModal
          type={assumeUserFieldType}
          txtAssumeUser={txtAssumeUser}
          isShowingModal={isShowingModal}
          handleClose={onAssumeModalClose}
          onChange={onAssumeTextChange}
          handleSave={onAssumeSubmit}
        />
      )}
      {isShowingModalUnassume && (
        <RemoveAssumeUserModal
          isShowingModalUnassume={isShowingModalUnassume}
          handleCloseUnassumeNo={onUnassumeModalNo}
          handleCloseUnassume={onUnassumeConfirm}
          assumeUserVal={assumeuser.username}
        />
      )}
      {cognitoGroups === undefined && (
        <div
          className={[
            headerBarStyles.infobar,
            subNav.pageHasLegacyHeaderOffset ? '' : domesticStyles.infobarFlush,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {renderAccountStatusBanner()}
        </div>
      )}
    </>
  );
}
